/**
 * Pure segment-derivation for StatusBreakdownBar (Fix Spec D) — extracted so
 * the "unknown key -> trailing Other segment" behavior is unit-testable
 * without rendering.
 */

import { STATUS_META } from '../types';

/**
 * Data color for the catch-all segment: gray (not one of the semantic
 * per-status hues, so it never gets confused with a real status), with a
 * lighter dark-mode shade for contrast against the dark track background
 * (`bg-gray-700` — see StatusBreakdownBar).
 */
export const OTHER_STATUS_BAR_CLASS = 'bg-gray-400 dark:bg-gray-500';
export const OTHER_STATUS_LABEL = 'Other';
export const OTHER_STATUS_KEY = '__other__';

export interface StatusSegment {
  key: string;
  label: string;
  barClass: string;
  minutes: number;
}

/**
 * STATUS_META keys render first, in STATUS_META's display order. Any key
 * present in `minutesByStatus` but absent from STATUS_META — a status the
 * workflow enum gained without a matching frontend update — is summed into
 * one trailing "Other" segment instead of being dropped from the bar
 * (Fix Spec D: visible, not lossy).
 */
export function deriveStatusSegments(minutesByStatus: Record<string, number>): StatusSegment[] {
  const known = new Set(STATUS_META.map((s) => s.key));
  const segments: StatusSegment[] = [];
  for (const s of STATUS_META) {
    const minutes = Number(minutesByStatus[s.key] || 0);
    if (minutes > 0) segments.push({ key: s.key, label: s.label, barClass: s.barClass, minutes });
  }
  let otherMinutes = 0;
  for (const key of Object.keys(minutesByStatus)) {
    if (known.has(key)) continue;
    otherMinutes += Number(minutesByStatus[key] || 0);
  }
  if (otherMinutes > 0) {
    segments.push({ key: OTHER_STATUS_KEY, label: OTHER_STATUS_LABEL, barClass: OTHER_STATUS_BAR_CLASS, minutes: otherMinutes });
  }
  return segments;
}

/**
 * Bar total = sum of every status's minutes, known or not (Fix Spec D).
 * Deliberately independent of the page's current time lens: this bar always
 * renders the FULL breakdown regardless of which lens is selected elsewhere
 * — that IS the breakdown semantics, not a bug to reconcile with the lens
 * toggle (Session-3 review confirmed this is intentional; keep it — see the
 * comment on StatusBreakdownBar).
 */
export function statusBreakdownTotal(minutesByStatus: Record<string, number>): number {
  let sum = 0;
  for (const m of Object.values(minutesByStatus)) sum += Number(m || 0);
  return sum;
}
